import { type CurveRef, updatesOf } from "@artis/sdk";
import { useArtis } from "@artis/sdk-react";
import { useEffect, useRef } from "react";

import { type CurveCache, curveKey, parseCurveKey, toApiOffset } from "../formula/curves";

type CurveWatcherDeps = {
  curves: CurveCache;
  subscribeExternals: (cb: (key: string, active: boolean) => void) => () => void;
};

export function useCurveWatcher(deps: CurveWatcherDeps) {
  const artis = useArtis();
  const depsRef = useRef(deps);
  depsRef.current = deps;

  useEffect(() => {
    if (!artis) return;
    const watcher = artis.v1.createWatcher((updates) => {
      for (const [ref, values] of updatesOf(updates, "curve")) {
        for (const [offset, value] of Object.entries(values)) {
          depsRef.current.curves.push(curveKey(ref.id, ref.field, offset), value);
        }
      }
    });
    const refsByKey = new Map<string, CurveRef>();
    const unsubscribe = depsRef.current.subscribeExternals((key, active) => {
      if (active) {
        const { product, field, offset } = parseCurveKey(key);
        if (!product || !field || !offset) return;
        const ref: CurveRef = { id: product, name: product, field, offsets: [toApiOffset(offset)] };
        refsByKey.set(key, ref);
        watcher.add(ref);
      } else {
        const ref = refsByKey.get(key);
        if (!ref) return;
        refsByKey.delete(key);
        watcher.remove(ref);
      }
    });
    return () => {
      unsubscribe();
      watcher.dispose();
    };
  }, [artis]);
}
